/**
 * A clean date-range picker: a trigger showing the range, opening a popover with masked DD/MM/YYYY text
 * inputs (type continuous digits — "26062026" auto-formats to 26/06/2026) and a month calendar with
 * month AND year navigation for range selection. NO native <input type="date"> (its OS calendar — Thai
 * Buddhist-era, no less — popped up over this one). Dates are ISO "YYYY-MM-DD" on the wire; min/max clamp.
 *
 * 31/7 — the masked-input mechanics, the fixed-popover behavior, and the calendar grid's nav/keyboard
 * handling now live in dateFieldShared.tsx, shared with the single-date DatePicker (see that file's own
 * banner). This component's own shape/behavior is unchanged by that move — see dateFieldShared.tsx's
 * top comment for what moved and why.
 */

import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  format,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  isWithinInterval,
  parseISO,
  startOfMonth,
} from "date-fns";
import { CalendarDays } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  CalendarHeader,
  WEEKDAYS,
  clamp,
  iso,
  isoToDMY,
  maskDigits,
  prettyIso,
  useCalendarNav,
  useFixedPopover,
} from "./dateFieldShared";

export interface DateRange {
  start: string; // ISO YYYY-MM-DD
  end: string;
}

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  // The `| undefined` is deliberate under exactOptionalPropertyTypes: DateRangePicker forwards min/max
  // straight down to TypedInputs/Calendar (which reuse this same props type), so they arrive as an EXPLICIT
  // undefined rather than an absent key. Both sub-components read them as "no bound" (`if (min && s < min)`),
  // which is exactly what absent means. AuditPage already renders the picker with no min/max at all.
  min?: string | undefined;
  max?: string | undefined;
  className?: string;
  /** Accessible name for the trigger + popover (the visible field label sits outside the widget). */
  "aria-label"?: string;
}

export function DateRangePicker({
  value,
  onChange,
  min,
  max,
  className,
  ...aria
}: DateRangePickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const coords = useFixedPopover({
    open,
    onClose: () => setOpen(false),
    containerRef: ref,
    triggerRef,
    popoverRef,
  });

  return (
    <div ref={ref} className={cn("relative inline-flex", className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={aria["aria-label"]}
        className={cn(
          "inline-flex h-9 items-center gap-2 rounded-(--radius-sm) border border-(--border-input) bg-(--surface) px-3 text-(length:--text-sm) text-(--text)",
          "transition-colors hover:border-(--border-strong) focus-visible:border-(--brand) focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_var(--ring)]",
        )}
      >
        <CalendarDays className="size-4 shrink-0 text-(--text-subtle)" aria-hidden />
        <span className="tabular">
          {prettyIso(value.start)} <span className="text-(--text-subtle)">→</span>{" "}
          {prettyIso(value.end)}
        </span>
      </button>

      {open && (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label={aria["aria-label"] ?? t("daterange.label")}
          // No onBlur-based close: clicking a DISABLED day fires NO mousedown (disabled controls suppress
          // it) yet bounces focus to an element OUTSIDE the widget, so a relatedTarget check here closed
          // the picker on every greyed-out-day click (the live "can't select dates" bug). Closing is
          // owned entirely by the document mousedown-outside listener + Escape (both below) + the trigger
          // toggle — all of which fire only on genuine outside/dismiss intent.
          className="fixed z-(--z-dropdown) flex flex-col gap-3 rounded-(--radius-lg) border border-(--border) bg-(--surface-raised) p-4 shadow-(--shadow-lg)"
          style={coords ? { top: coords.top, left: coords.left } : { visibility: "hidden" }}
        >
          <TypedInputs value={value} onChange={onChange} min={min} max={max} />
          <Calendar value={value} onChange={onChange} min={min} max={max} />
        </div>
      )}
    </div>
  );
}

/** Masked DD/MM/YYYY text inputs — type digits straight through; commits on a complete, valid date, and
 * shows an inline aria-live hint when a complete entry is invalid / out of range (no more silent no-op). */
function TypedInputs({ value, onChange, min, max }: DateRangePickerProps) {
  const { t } = useTranslation();
  const id = useId();
  const [from, setFrom] = useState(() => isoToDMY(value.start));
  const [to, setTo] = useState(() => isoToDMY(value.end));
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setFrom(isoToDMY(value.start)), [value.start]);
  useEffect(() => setTo(isoToDMY(value.end)), [value.end]);

  function maskAndCommit(raw: string, which: "start" | "end", setLocal: (s: string) => void) {
    const { formatted, digits } = maskDigits(raw);
    setLocal(formatted);
    if (digits.length < 8) {
      setError(null);
      return;
    }
    const isoStr = `${digits.slice(4, 8)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
    const d = parseISO(isoStr);
    if (!isValid(d) || iso(d) !== isoStr) {
      setError(t("daterange.invalid"));
      return;
    }
    const s = clamp(isoStr, min, max);
    const next = which === "start" ? { start: s, end: value.end } : { start: value.start, end: s };
    if (next.start > next.end) {
      setError(t("daterange.order"));
      return;
    }
    // Out-of-range typed dates are clamped to the allowed window — say so instead of snapping silently.
    setError(s !== isoStr ? t("daterange.adjusted") : null);
    onChange(next);
  }

  const inputCls =
    "h-8 w-28 rounded-(--radius-sm) border border-(--border-input) bg-(--surface) px-2 text-(length:--text-sm) tabular text-(--text) focus-visible:border-(--brand) focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_var(--ring)]";

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-end gap-2">
        <label className="flex flex-col gap-1 text-(length:--text-2xs) font-medium text-(--text-muted)">
          {t("daterange.from")}
          <input
            id={`${id}-from`}
            inputMode="numeric"
            placeholder="DD/MM/YYYY"
            value={from}
            onChange={(e) => maskAndCommit(e.target.value, "start", setFrom)}
            className={inputCls}
          />
        </label>
        <span className="pb-2 text-(--text-subtle)">→</span>
        <label className="flex flex-col gap-1 text-(length:--text-2xs) font-medium text-(--text-muted)">
          {t("daterange.to")}
          <input
            id={`${id}-to`}
            inputMode="numeric"
            placeholder="DD/MM/YYYY"
            value={to}
            onChange={(e) => maskAndCommit(e.target.value, "end", setTo)}
            className={inputCls}
          />
        </label>
      </div>
      {error && (
        <p role="alert" className="text-(length:--text-2xs) text-(--danger-strong-fg)">
          {error}
        </p>
      )}
    </div>
  );
}

function Calendar({ value, onChange, min, max }: DateRangePickerProps) {
  // Open on the END month (the most RECENT boundary, near `max`/today) — its days are enabled. Opening on
  // value.start (often ~12 months back, before `min`) showed a month of greyed-out days, so the first
  // day a user clicked was disabled and the picker dismissed.
  const endD = parseISO(value.end);
  const startD = parseISO(value.start);
  const initialMonth = isValid(endD) ? endD : isValid(startD) ? startD : new Date();
  const [month, setMonth] = useState(() => startOfMonth(initialMonth));
  const [anchor, setAnchor] = useState<Date | null>(null);
  const { days, focusedDate, setFocusedDate, gridRef, onGridKeyDown } = useCalendarNav({
    month,
    setMonth,
    min,
    max,
    initialFocusedDate: initialMonth,
  });

  const selStart = parseISO(value.start);
  const selEnd = parseISO(value.end);

  function pick(day: Date) {
    const s = iso(day);
    if (min && s < min) return;
    if (max && s > max) return;
    if (anchor == null) {
      setAnchor(day);
      onChange({ start: s, end: s });
    } else {
      const a = iso(anchor);
      onChange(s < a ? { start: s, end: a } : { start: a, end: s });
      setAnchor(null);
    }
  }

  return (
    <div className="flex w-[16rem] flex-col gap-2">
      <CalendarHeader month={month} setMonth={setMonth} />

      <div ref={gridRef} onKeyDown={onGridKeyDown} className="grid grid-cols-7 gap-0.5">
        {WEEKDAYS.map((d, i) => (
          <span
            key={i}
            className="grid h-7 place-items-center text-(length:--text-2xs) font-medium text-(--text-subtle)"
          >
            {d}
          </span>
        ))}
        {days.map((day) => {
          const inMonth = day.getMonth() === month.getMonth();
          const s = iso(day);
          const disabled = (min && s < min) || (max && s > max);
          const isStart = isValid(selStart) && isSameDay(day, selStart);
          const isEnd = isValid(selEnd) && isSameDay(day, selEnd);
          const isFocusTarget = isSameDay(day, focusedDate);
          const inRange =
            isValid(selStart) &&
            isValid(selEnd) &&
            !isBefore(day, selStart) &&
            !isAfter(day, selEnd) &&
            isWithinInterval(day, { start: selStart, end: selEnd });
          return (
            <button
              type="button"
              key={s}
              data-day={s}
              disabled={!!disabled}
              tabIndex={isFocusTarget ? 0 : -1}
              onClick={() => pick(day)}
              onFocus={() => {
                if (!isSameDay(day, focusedDate)) setFocusedDate(day);
              }}
              aria-label={format(day, "MMMM d, yyyy")}
              aria-pressed={isStart || isEnd}
              aria-selected={isStart || isEnd}
              {...(isStart || isEnd ? { "aria-current": "date" as const } : {})}
              className={cn(
                "grid h-8 w-8 place-items-center rounded-(--radius-sm) text-(length:--text-xs) tabular transition-colors",
                disabled && "cursor-not-allowed opacity-30",
                !inMonth && "text-(--text-subtle)",
                inMonth && !inRange && "text-(--text) hover:bg-(--surface-hover)",
                inRange && !isStart && !isEnd && "bg-(--brand-subtle) text-(--brand-strong)",
                (isStart || isEnd) && "bg-(--brand) font-semibold text-(--text-onbrand)",
              )}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
